<template>
  <div>
    <div class="pb-3 pt-3">
      <v-alert v-if="hasStabSchematisationById(revisionId)" dark type="info" density="compact">
        This stage configuration is used inside a stab schematisation, therefore you can not edit
        the table below.
      </v-alert>
    </div>
    <v-row align="center">
      <v-col>
        <v-data-table
          density="compact"
          :headers="headers"
          :loading="loading"
          :items="shownStageConfigurations"
          :items-per-page="-1"
          hide-default-footer
          @contextmenu:row="show"
          @hover:row="false"
          class="data-table"
          disable-sort
        >
          <template #loader="{ isActive }">
            <v-progress-linear :active="isActive" indeterminate color="primary" />
          </template>
          <template v-slot:item="{ item, index }">
            <tr @click="selectedItem = item" @contextmenu="show($event, item)">
              <td class="text-center">
                <span>
                  {{ item.number }}
                </span>
              </td>
              <td>
                <DataTableField
                  @update="addDirty(item, 'name')"
                  :dirtyClass="getDirtyClass(item, 'name')"
                  @set="setParameter"
                  :item="item"
                  parameter="name"
                  type="string"
                  :disabled="isFieldDisabled({ index, header: 'name', item })"
                />
              </td>
              <td>
                <CheckBoxField
                  @update="addDirty(item, 'topo_profile')"
                  :dirtyClass="getDirtyClass(item, 'topo_profile')"
                  @set="setParameter"
                  :item="item"
                  parameter="topo_profile"
                  :disabled="isFieldDisabled({ index, header: 'topo_profile', item })"
                />
              </td>
              <td>
                <CheckBoxField
                  @update="addDirty(item, 'soil_profile')"
                  :dirtyClass="getDirtyClass(item, 'soil_profile')"
                  @set="setParameter"
                  :item="item"
                  parameter="soil_profile"
                  :disabled="isFieldDisabled({ index, header: 'soil_profile', item })"
                />
              </td>
              <td>
                <OptionField
                  @update="addDirty(item, 'phreatic_line')"
                  :dirtyClass="getDirtyClass(item, 'phreatic_line')"
                  @set="setParameter"
                  :item="item"
                  parameter="phreatic_line"
                  :options="options"
                  :disabled="isFieldDisabled({ index, header: 'phreatic_line', item })"
                />
              </td>
              <td>
                <OptionField
                  @update="addDirty(item, 'head_line_1')"
                  :dirtyClass="getDirtyClass(item, 'head_line_1')"
                  @set="setParameter"
                  :item="item"
                  parameter="head_line_1"
                  :options="options"
                  :disabled="isFieldDisabled({ index, header: 'head_line_1', item })"
                />
              </td>
              <td>
                <OptionField
                  @update="addDirty(item, 'head_line_2')"
                  :dirtyClass="getDirtyClass(item, 'head_line_2')"
                  @set="setParameter"
                  :item="item"
                  parameter="head_line_2"
                  :options="options"
                  :disabled="
                    isFieldDisabled({ index, header: 'head_line_2', item }) ||
                    item.head_line_1 === 'no'
                  "
                />
              </td>
              <td>
                <CheckBoxField
                  @update="addDirty(item, 'cut_soil_layers')"
                  :dirtyClass="getDirtyClass(item, 'cut_soil_layers')"
                  @set="setParameter"
                  :item="item"
                  parameter="cut_soil_layers"
                  :disabled="isFieldDisabled({ index, header: 'cut_soil_layers', item })"
                />
              </td>
              <td>
                <CheckBoxField
                  @update="addDirty(item, 'states')"
                  :dirtyClass="getDirtyClass(item, 'states')"
                  @set="setParameter"
                  :item="item"
                  parameter="states"
                  :disabled="isFieldDisabled({ index, header: 'states', item })"
                />
              </td>
              <td>
                <CheckBoxField
                  @update="addDirty(item, 'loads')"
                  :dirtyClass="getDirtyClass(item, 'loads')"
                  @set="setParameter"
                  :item="item"
                  parameter="loads"
                  :disabled="isFieldDisabled({ index, header: 'loads', item })"
                />
              </td>
              <td>
                <CheckBoxField
                  @update="addDirty(item, 'reinforcement')"
                  :dirtyClass="getDirtyClass(item, 'reinforcement')"
                  @set="
                    setParameter($event), setNextStageTopoSoilProfile({ event: $event, item: item })
                  "
                  :item="item"
                  parameter="reinforcement"
                  :disabled="isFieldDisabled({ index, header: 'reinforcement', item })"
                />
              </td>
              <td>
                <CheckBoxField
                  @update="addDirty(item, 'overview')"
                  :dirtyClass="getDirtyClass(item, 'overview')"
                  @set="setParameter"
                  :item="item"
                  parameter="overview"
                  :disabled="isFieldDisabled({ index, header: 'overview', item })"
                />
              </td>
              <td>
                <OptionField
                  @update="addDirty(item, 'calculation')"
                  :dirtyClass="getDirtyClass(item, 'calculation')"
                  @set="setParameter"
                  :item="item"
                  parameter="calculation"
                  :options="options"
                  :disabled="isFieldDisabled({ index, header: 'calculation', item })"
                />
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <TableActionMenu
      @update:show-menu="showMenu = $event"
      v-if="!hasStabSchematisationById(revisionId)"
      :addButton="false"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      @action="deleteObj(selectedItem.id)"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import DirtyFieldsMixIn from '@/mixins/DirtyFieldsMixIn.vue'
import ShowActionMenuMixin from '@/mixins/ShowActionMenuMixin.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import OptionField from '@/components/form/OptionField.vue'
import CheckBoxField from '@/components/form/CheckBoxField.vue'
import DataTableField from '@/components/form/DataTableField.vue'

export default {
  name: 'StageConfigurationsTable',
  components: {
    TableActionMenu,
    OptionField,
    CheckBoxField,
    DataTableField
  },
  mixins: [DirtyFieldsMixIn, ShowActionMenuMixin],
  data: () => ({
    headers: [
      { title: 'Number', align: 'center', width: '5%' },
      { title: 'Name', width: '20%' },
      { title: 'Topo profile', align: 'center', width: '6%' },
      { title: 'Soil profiles', align: 'center', width: '6%' },
      { title: 'Phreatic line', width: '8%' },
      { title: 'Head line 1', width: '8%' },
      { title: 'Head line 2', width: '8%' },
      { title: 'Cut soil layers', align: 'center', width: '6%' },
      { title: 'States', align: 'center', width: '6%' },
      { title: 'Loads', align: 'center', width: '6%' },
      { title: 'Reinforcements', align: 'center', width: '6%' },
      { title: 'Overview', align: 'center', width: '6%' },
      { title: 'Calculation' }
    ],
    disabledStatus: false,
    itemsPerPage: -1,
    nameStageEmptyClass: 'border-bottom-red'
  }),
  computed: {
    ...mapGetters('stageConfigurationRevision', {
      hasStabSchematisationById: 'hasStabSchematisationById'
    }),
    ...mapState('stageConfiguration', {
      loading: (state) => state.loading,
      count: (state) => state.count,
      items: (state) => state.items,
      options: (state) => state.options
    }),
    visibleHeaders() {
      return this.headers.filter((header) => header.field === true)
    },
    shownStageConfigurations() {
      return this.count ? this.items : []
    },
    revisionId() {
      return parseInt(this.$route.params.revisionId)
    }
  },
  watch: {
    shownStageConfigurations(newValue, oldValue) {
      if (this.shownStageConfigurations.length && oldValue.length) {
        let item = this.shownStageConfigurations[0]
        let parameter = 'topo_profile'
        this.setParameter({
          value: true,
          item: item,
          parameter: parameter
        })
      }
    },
    async revisionId() {
      if (!this.loading) {
        await this.resetState()
        await this.fetchOptions()
        await this.fetchItems(this.revisionId)
      }
    }
  },
  methods: {
    ...mapActions('stageConfiguration', {
      handleAdd: 'add',
      handleDeleteObj: 'deleteItem',
      create: 'create',
      fetchOptions: 'fetchOptions',
      fetchItems: 'fetchItems',
      update: 'update',
      emptyDirtyItems: 'emptyDirtyItems',
      toggleWriteLoading: 'toggleWriteLoading',
      handleSetParameter: 'setParameter',
      resetState: 'resetState'
    }),
    ...mapActions('revision', {
      fetchRevision: 'fetchRevision'
    }),
    setNextStageTopoSoilProfile({ item }) {
      const index = this.items.findIndex((stage) => stage.id === item.id)
      const nextStage = this.items[index + 1]
      if (nextStage && item.reinforcement) {
        this.handleSetParameter({
          value: true,
          item: nextStage,
          parameter: 'topo_profile'
        })
        this.handleSetParameter({
          value: true,
          item: nextStage,
          parameter: 'soil_profile'
        })
      }
    },
    async saveChanges() {
      try {
        await this.update()
        await this.create()
        this.emptyDirtyItems()
        this.dirtyItems = []
      } catch (e) {
        console.log(e)
        this.toggleWriteLoading()
      }
    },
    isFieldDisabled({ header, index }) {
      let hasReinforcementInPreviousStage = false
      const previousStage = this.items[index - 1]
      if (['topo_profile', 'soil_profile'].includes(header) && previousStage?.reinforcement) {
        hasReinforcementInPreviousStage = true
      }
      return (
        this.hasStabSchematisationById(this.revisionId) ||
        (index === 0 && header === 'topo_profile') ||
        hasReinforcementInPreviousStage
      )
    },
    async reconfigTopoProfile() {
      if (this.items.length === 0) {
        return
      }
      for (const [index, stage] of this.items.entries()) {
        if (index < 1) {
          continue
        }
        const previousStage = this.items[index - 1]
        if (previousStage.reinforcement && !stage.topo_profile) {
          await this.handleSetParameter({
            value: true,
            item: stage,
            parameter: 'topo_profile'
          })
          await this.handleSetParameter({
            value: true,
            item: stage,
            parameter: 'soil_profile'
          })
        }
      }
    },
    async setParameter({ value, item, parameter }) {
      await this.handleSetParameter({ value, item, parameter })
      if (parameter === 'reinforcement' && value === true) {
        await this.reconfigTopoProfile()
      }
    },
    async add() {
      await this.handleAdd()
      await this.reconfigTopoProfile()
    },
    async deleteObj(id) {
      await this.handleDeleteObj(id)
      await this.reconfigTopoProfile()
    }
  },
  async mounted() {
    await this.resetState()
    await this.fetchOptions()
    if (this.revisionId) {
      await this.fetchItems(this.revisionId)
    }
  }
}
</script>
<style scoped>
.data-table:deep(th) {
  text-align: left;
  font-size: 0.75rem;
  font-family: Roboto, sans-serif;
  font-weight: bold !important;
  color: rgba(0, 0, 0, 0.6);
}

.data-table:deep(td) {
  padding: 0 !important;
}

.data-table:deep(th) {
  padding: 0 !important;
}
</style>
